import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatMoney, type CreditCustomer, type CreditTransaction } from '@/lib/credit';

interface Props {
  customers: CreditCustomer[];
  /** Solde signé par client : > 0 = dette, < 0 = fonds détenus. */
  balances: Record<number, number>;
  /** Opérations du carnet (pour calculer ce qui a été encaissé aujourd'hui). */
  transactions?: CreditTransaction[];
  currency: string;
  search: string;
  /** « Il prend à crédit » : ajoute une dette. */
  onNewCredit: (c: CreditCustomer) => void;
  /** « Il paie » : choice entre paiement de dette et dépôt de fonds. */
  onPay: (c: CreditCustomer) => void;
  /** « Tout est payé » : solde la dette restante. */
  onSettleDebt: (c: CreditCustomer) => void;
  onQr: (c: CreditCustomer) => void;
  onAddCustomer: () => void;
}

/** Vrai si la date ISO tombe dans la période choisie (heure locale). */
function inPeriod(iso: string, period: 'today' | '7d' | 'month'): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  if (period === 'today') {
    return (
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    );
  }
  if (period === '7d') {
    const start = new Date(now.getTime() - 7 * 86400000);
    return d >= start && d <= now;
  }
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

/**
 * CarnetView — vue « grand format » pensée pour un commerçant qui lit peu :
 * gros caractères, une grande carte par client, deux grands boutons d'action.
 * Couleurs sans ambiguïté : rouge = le client doit, vert = il a de l'argent
 * chez vous, gris = à jour.
 */
export default function CarnetView({
  customers, balances, transactions = [], currency, search,
  onNewCredit, onPay, onSettleDebt, onQr, onAddCustomer,
}: Props) {
  const { t } = useTranslation();
  const query = search.trim().toLowerCase();
  const nameOf = (c: CreditCustomer) => c.full_name || 'Client';
  const [collectedPeriod, setCollectedPeriod] = useState<'today' | '7d' | 'month'>('today');
  const filtered = customers.filter(
    (c) => !query || nameOf(c).toLowerCase().includes(query) || (c.phone || '').includes(query),
  );

  // Ce qui est rentré sur la période choisie : paiements de dette + dépôts de fonds.
  const collectedTx = transactions.filter(
    (tx) => (tx.type === 'payment' || tx.type === 'deposit') && inPeriod(tx.created_at, collectedPeriod),
  );
  const collected = collectedTx.reduce((s, tx) => s + Math.abs(Number(tx.amount) || 0), 0);
  const periodLabel = collectedPeriod === 'today'
    ? t('crf.collected_today', { defaultValue: 'Encaissé aujourd\u2019hui' })
    : collectedPeriod === '7d'
      ? t('crf.collected_7d', { defaultValue: 'Encaissé sur 7 jours' })
      : t('crf.collected_month', { defaultValue: 'Encaissé ce mois-ci' });

  // Les plus gros débiteurs (top 3) pour les mettre en avant.
  const topDebtors = [...customers]
    .filter((c) => (balances[c.id] || 0) > 0)
    .sort((a, b) => (balances[b.id] || 0) - (balances[a.id] || 0))
    .slice(0, 3);
  const rankOf = new Map(topDebtors.map((c, i) => [c.id, i + 1]));

  // Ceux qui doivent d'abord, puis les autres.
  const sorted = [...filtered].sort((a, b) => (balances[b.id] || 0) - (balances[a.id] || 0));

  if (customers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-2xl text-center px-6">
        <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center mb-4">
          <i className="ri-book-2-line text-4xl text-primary-600"></i>
        </div>
        <h3 className="text-xl font-bold font-heading text-foreground-800 mb-2">{t('crf.ledger_empty_title', { defaultValue: 'Ton carnet est vide' })}</h3>
        <p className="text-sm text-foreground-500 mb-6 max-w-sm">
          {t('crf.ledger_empty_desc', { defaultValue: 'Ajoute ton premier client, puis note quand il prend à crédit ou quand il paie.' })}
        </p>
        <button
          onClick={onAddCustomer}
          className="px-6 py-3.5 rounded-full bg-primary-500 text-background-50 text-base font-bold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer flex items-center gap-2"
        >
          <i className="ri-user-add-line text-lg"></i>{t('crf.add_customer', { defaultValue: 'Ajouter un client' })}
        </button>
      </div>
    );
  }

  if (sorted.length === 0) {
    return (
      <div className="py-14 text-center bg-background-50 border border-background-200/70 rounded-2xl">
        <i className="ri-search-eye-line text-3xl text-foreground-300"></i>
        <p className="mt-2 text-base text-foreground-500">{t('crf.no_client_found', { defaultValue: 'Aucun client trouvé' })}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Bandeau : encaissé aujourd'hui + plus gros débiteurs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
        <div className="md:col-span-1 bg-secondary-500 text-background-50 rounded-2xl p-5 flex flex-col justify-center">
          <div className="flex items-center gap-2 text-sm font-medium text-background-50/80">
            <i className="ri-hand-coin-line text-lg"></i>
            {periodLabel}
          </div>
          <div className="text-3xl md:text-4xl font-bold font-heading mt-1">{formatMoney(collected, currency)}</div>
          <div className="text-xs text-background-50/80 mt-1">
            {collectedTx.length === 0
              ? t('crf.no_collection_period', { defaultValue: 'Aucun paiement reçu sur la période' })
              : `${collectedTx.length} ${t('crf.operations', { defaultValue: 'opération(s)' })}`}
          </div>
          {/* Sélecteur de période : jour / 7 jours / mois */}
          <div className="flex items-center gap-1 bg-black/15 rounded-full p-1 mt-3">
            {([
              { key: 'today', label: t('crf.period_today', { defaultValue: 'Aujourd\u2019hui' }) },
              { key: '7d', label: t('crf.period_7d', { defaultValue: '7 jours' }) },
              { key: 'month', label: t('crf.period_month', { defaultValue: 'Mois' }) },
            ] as const).map((p) => (
              <button
                key={p.key}
                onClick={() => setCollectedPeriod(p.key)}
                className={`flex-1 px-2 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  collectedPeriod === p.key ? 'bg-background-50 text-secondary-700' : 'text-background-50/80 hover:text-background-50'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="md:col-span-2 bg-background-50 border border-background-200/70 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-8 h-8 rounded-lg bg-red-100 text-red-700 flex items-center justify-center flex-shrink-0">
              <i className="ri-alarm-warning-line"></i>
            </span>
            <div>
              <div className="text-sm font-bold text-foreground-950">{t('crf.top_debtors', { defaultValue: 'Les plus gros débiteurs' })}</div>
              <div className="text-xs text-foreground-500">{t('crf.top_debtors_hint', { defaultValue: 'Les clients qui te doivent le plus — relance-les en priorité.' })}</div>
            </div>
          </div>
          {topDebtors.length === 0 ? (
            <p className="text-sm text-foreground-400">{t('crf.no_debtors', { defaultValue: 'Personne ne te doit rien pour le moment. 👍' })}</p>
          ) : (
            <div className="space-y-2">
              {topDebtors.map((c, i) => (
                <Link
                  key={c.id}
                  to={`/dashboard/credit/customer/${c.id}`}
                  className="flex items-center gap-3 no-underline rounded-xl bg-red-50/60 border border-red-100 px-3 py-2 hover:bg-red-50 transition-colors"
                >
                  <span className="w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {i + 1}
                  </span>
                  <span className="flex-1 min-w-0 text-base font-bold text-foreground-950 truncate">{nameOf(c)}</span>
                  <span className="text-base font-bold text-red-700 whitespace-nowrap">
                    {formatMoney(balances[c.id] || 0, currency)}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Cartes clients */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
      {sorted.map((c) => {
        const bal = balances[c.id] || 0;
        const debt = Math.max(0, bal);
        const store = Math.max(0, -bal);
        const isDebt = debt > 0;
        const isStore = store > 0;
        const rank = rankOf.get(c.id);

        return (
          <div
            key={c.id}
            className={`bg-background-50 border rounded-2xl p-4 md:p-5 ${rank ? 'border-red-300 ring-2 ring-red-100' : 'border-background-200/70'}`}
          >
            {rank && (
              <div className="flex items-center gap-1.5 mb-2 text-xs font-bold text-red-600">
                <i className="ri-fire-line"></i>
                {t('crf.top_debtor_badge', { defaultValue: 'Top débiteur #{{n}}', n: rank })}
              </div>
            )}
            {/* Ligne 1 : client + montant */}
            <div className="flex items-start justify-between gap-3">
              <Link to={`/dashboard/credit/customer/${c.id}`} className="flex items-center gap-3 min-w-0 no-underline">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 text-xl font-bold ${isDebt ? 'bg-red-100 text-red-700' : isStore ? 'bg-emerald-100 text-emerald-700' : 'bg-background-100 text-foreground-500'}`}>
                  {nameOf(c).charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-lg md:text-xl font-bold font-heading text-foreground-950 truncate">{nameOf(c)}</div>
                  <div className="text-sm text-foreground-400 truncate">
                    {c.phone || t('crf.no_phone', { defaultValue: 'Sans téléphone' })}
                  </div>
                </div>
              </Link>

              {/* Grand montant coloré */}
              <div className={`text-right flex-shrink-0 rounded-xl px-3 py-2 ${isDebt ? 'bg-red-50' : isStore ? 'bg-emerald-50' : 'bg-background-100'}`}>
                <div className={`text-2xl md:text-3xl font-bold font-heading leading-none ${isDebt ? 'text-red-700' : isStore ? 'text-emerald-700' : 'text-foreground-400'}`}>
                  {formatMoney(isDebt ? debt : store, currency)}
                </div>
                <div className={`text-xs font-semibold mt-1 ${isDebt ? 'text-red-600' : isStore ? 'text-emerald-700' : 'text-foreground-400'}`}>
                  {isDebt ? t('crf.must_pay', { defaultValue: 'Doit' }) : isStore ? t('crf.has_here', { defaultValue: 'A chez vous' }) : t('crf.up_to_date', { defaultValue: 'À jour' })}
                </div>
              </div>
            </div>

            {/* Gros boutons d'action */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-4">
              <button
                onClick={() => onNewCredit(c)}
                className="h-14 rounded-xl bg-primary-500 text-background-50 text-base font-bold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-add-line text-xl"></i>{t('crf.takes_credit', { defaultValue: 'Il prend à crédit' })}
              </button>
              <button
                onClick={() => onPay(c)}
                className="h-14 rounded-xl bg-accent-500 text-background-50 text-base font-bold whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-hand-coin-line text-xl"></i>{t('crf.he_pays', { defaultValue: 'Il paie' })}
              </button>
            </div>

            {/* « Tout est payé » : uniquement s'il y a une dette */}
            {isDebt && (
              <button
                onClick={() => onSettleDebt(c)}
                className="w-full mt-2.5 h-12 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-bold whitespace-nowrap hover:bg-emerald-100 transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-check-double-line text-lg"></i>{t('crf.settle_all', { defaultValue: 'Tout est payé' })}
              </button>
            )}

            {/* QR discret (optionnel) */}
            <div className="mt-2 flex justify-end">
              <button
                onClick={() => onQr(c)}
                title={t('crf.qr_client', { defaultValue: 'QR client' })}
                className="text-xs text-foreground-400 hover:text-foreground-700 transition-colors cursor-pointer flex items-center gap-1"
              >
                <i className="ri-qr-code-line"></i>{t('crf.qr_client', { defaultValue: 'QR client' })}
              </button>
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}